import { type Colors } from '@factorialco/deprecated-design-system'
import { findIndex, uniq } from 'lodash'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import workAreas from 'modules/locations/store/collections/workAreas'
import { type Duration, momentToDuration } from 'modules/shift_management/lib/hoursToDuration'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import PlanningVersions from 'modules/shift_management/store/internal/collections/PlanningVersions'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import EmployeeWithShifts, {
  TShiftsRow,
} from 'modules/shift_management/store/internal/models/EmployeeWithShifts'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'

import ShiftPermissions from './ShiftPermissions'

export interface EmployeeShift {
  employee: EmployeeWithShifts | undefined
  shift: Shift
}

interface UrlParams {
  id: number
}

interface Props {
  id: number
  title: string
  job?: string
  maximumWeeklyHours?: number
  subtitle?: string
  avatar?: string
  employees: EmployeeWithShifts[]
  permissions: ShiftPermissions
  key?: string
  urlParams?: UrlParams
  fragment?: TShiftsRow
}

class ShiftsRow {
  id: number
  title: string
  job?: string
  maximumWeeklyHours?: number
  subtitle?: string
  avatar?: string
  employees: EmployeeWithShifts[]
  // TODO: Handle permissions in a different way for each row depending on employees, areas, etc
  permissions: ShiftPermissions
  // Use this to iterate or select rows to avoid null ids like "No work area" rows
  key: string
  urlParams?: UrlParams
  fragment?: TShiftsRow

  constructor(props: Props) {
    this.id = props.id
    this.title = props.title
    this.job = props.job
    this.maximumWeeklyHours = props.maximumWeeklyHours
    this.subtitle = props.subtitle
    this.avatar = props.avatar
    this.employees = props.employees
    this.permissions = props.permissions
    this.key = props.key || this.id.toString()
    this.urlParams = props.urlParams

    makeObservable(this, {
      employee: computed,
      shifts: computed,
      hasShifts: computed,
      leaves: computed,
      holidays: computed,
      restDays: computed,
      planningVersions: computed,
      employeeIds: computed,
      visibleShifts: computed,
      visibleShiftCollection: computed,
      orderedShifts: computed,
      nonBackupShifts: computed,
      plannedBreaksCollection: computed,
    })
  }

  static buildFromGraphQL(shiftsRow: TShiftsRow): ShiftsRow {
    return new ShiftsRow({
      id: shiftsRow.id,
      title: shiftsRow.fullName || '',
      maximumWeeklyHours: shiftsRow.referenceContract?.maximumWeeklyHours || undefined,
      avatar: shiftsRow.avatar?.url || '',
      permissions: ShiftPermissions.buildFromGraphQL(shiftsRow) || {},
      job: EmployeeWithShifts.employeeJob(shiftsRow),
      employees: [EmployeeWithShifts.buildFromGraphQL(shiftsRow)],
      fragment: shiftsRow,
    })
  }

  // Only works for the employee view because the employee id is the key.
  get employee(): EmployeeWithShifts | undefined {
    return this.employees.find((employee) => employee.id.toString() === this.key)
  }

  get shifts(): Shifts {
    const shifts = this.employees.flatMap((e: EmployeeWithShifts) => e.shifts.shifts)
    return new Shifts(shifts)
  }

  get hasShifts(): boolean {
    return !!this.shifts.shiftIds.length
  }

  get leaves(): Leaves {
    const leaves = this.employees.flatMap((e) => e.leaves.leaves)
    return new Leaves(leaves)
  }

  get holidays(): Holidays {
    const holidays = this.employees.flatMap((e) => e.holidays.holidays)
    return new Holidays(holidays)
  }

  get restDays(): RestDays {
    const restDays = this.employees.flatMap((e) => e.restDays.restDays)
    return new RestDays(restDays)
  }

  get planningVersions(): PlanningVersions {
    const planningVersions = this.employees.flatMap((e) => e.planningVersions.planningVersions)
    return new PlanningVersions(planningVersions)
  }

  get employeeIds(): number[] {
    // Use this.shifts instead of this.employees because we want to refresh or consider only those employees with shifts in the row
    return uniq([...this.shifts.employeeIds, ...this.restDays.employeeIds])
  }

  // For the work area view, this will be used to get the location id
  get keyReference(): number {
    const firstKeyItem = this.key.split('-')[0]
    return Number(firstKeyItem)
  }

  get visibleShifts(): Shift[] {
    return this.employees.flatMap((employee: EmployeeWithShifts) => employee.visibleShifts())
  }

  get visibleShiftCollection(): Shifts {
    return new Shifts(this.visibleShifts)
  }

  get orderedShifts(): Shift[] {
    return orderBy(this.visibleShifts, ['_startAt', '_endAt'], ['asc', 'asc'])
  }

  get nonBackupShifts(): Shift[] {
    return this.shifts.shifts.filter((s) => !s.isBackup)
  }

  get plannedBreaksCollection(): PlannedBreaks {
    return this.visibleShiftCollection.plannedBreaksCollection
  }

  shiftsByDate(date: moment.Moment, view: View): Shift[] {
    return this.visibleShiftCollection.filterByDate(date, view)
  }

  orderedShiftsByDate(date: moment.Moment, view: View): Shift[] {
    return orderBy(this.shiftsByDate(date, view), ['_startAt', '_endAt'], ['asc', 'asc'])
  }

  orderedEmployeeShiftsByDate(date: moment.Moment, view: View): EmployeeShift[] {
    const orderedShifts = this.orderedShiftsByDate(date, view)

    return orderedShifts.map((shift) => {
      const employee = this.employees.find((e) => e.id === shift.employeeId)
      return { employee, shift: shift }
    })
  }

  nightShiftsPeriodBefore(date: moment.Moment): Shift[] {
    return this.visibleShiftCollection.filterByNightShiftEndDate(date)
  }

  orderedNightShiftsPeriodBefore(date: moment.Moment): Shift[] {
    return orderBy(this.nightShiftsPeriodBefore(date), ['_endAt', '_startAt'], ['asc', 'asc'])
  }

  notFromPeriodBefore(date: moment.Moment, view: View): EmployeeShift[] {
    const allShiftsByDate = this.orderedShiftsByDate(date, view)
    const nightShiftsPeriodBefore = new Set(this.nightShiftsPeriodBefore(date))
    const shifts = allShiftsByDate.filter((shift) => !nightShiftsPeriodBefore.has(shift))

    return shifts.map((shift) => {
      const employee = this.employees.find((e) => e.id === shift.employeeId)
      return { employee, shift: shift }
    })
  }

  // FIXME: We will remove this once 'dev_shift_management_bank_of_hours' is done
  // And we will use hoursPerRangeAsMilliseconds instead
  //
  // Givin a period (week, month, 4-weeks) from firstDay to lastDay
  hoursPerRange(
    firstDay: moment.Moment,
    lastDay: moment.Moment,
    plannedBreaks: PlannedBreaks
  ): Duration {
    // (1) Duration of shifts within the Range
    const withinRange = this.visibleShiftCollection.filterByNotBackupAndWithinRange(
      firstDay,
      lastDay
    )
    const withinRangeDuration = withinRange.duration()

    // (2) Duration of the proportional part of shifts that
    // jump from one Range to the next

    const overnightToNext = this.visibleShiftCollection.filterByOvernightToNextRange(lastDay)
    const toNextDuration = overnightToNext.durationOvernightToNextWeek()

    // (3) Duration of the proportional part of shifts that
    // come from the previous Range to the current one
    const overnightFromPrevious =
      this.visibleShiftCollection.filterByOvernightFromPreviousRange(firstDay)
    const fromPreviousDuration = overnightFromPrevious.durationOvernightFromPreviousWeek()

    // (4) Unpaid planned breaks to subtract
    const allShiftIds = withinRange.shiftIds
      .concat(overnightToNext.shiftIds)
      .concat(overnightFromPrevious.shiftIds)

    // TODO: Use plannedBreaksCollection once 'dev_shift_management_graphql_migration' is done
    const selectedPlannedBreaks = this.plannedBreaksCollection.plannedBreaks.length
      ? this.plannedBreaksCollection.filterByShifts(allShiftIds)
      : plannedBreaks.filterByShifts(allShiftIds)
    const plannedBreaksMinutes = selectedPlannedBreaks.reduce((acc, plannedBreak) => {
      const { duration, breakConfigurationPaid } = plannedBreak
      if (!breakConfigurationPaid && duration) return acc + duration
      return acc
    }, 0)

    const plannedBreaksDuration = moment.duration(plannedBreaksMinutes, 'minutes')

    // (1) + (2) + (3) - (4)
    const totalDuration = withinRangeDuration
      .add(toNextDuration)
      .add(fromPreviousDuration)
      .subtract(plannedBreaksDuration)

    return momentToDuration(totalDuration)
  }

  private getShiftsAndDurationsForRange(
    firstDay: moment.Moment,
    lastDay: moment.Moment
  ): {
    shifts: Shift[]
    withinRangeDuration: moment.Duration
    toNextDuration: moment.Duration
    fromPreviousDuration: moment.Duration
  } {
    const withinRange = this.visibleShiftCollection.filterByNotBackupAndWithinRange(
      firstDay,
      lastDay
    )
    const withinRangeDuration = withinRange.duration()

    const overnightToNext = this.visibleShiftCollection.filterByOvernightToNextRange(lastDay)
    const toNextDuration = overnightToNext.durationOvernightToNextWeek()

    const overnightFromPrevious =
      this.visibleShiftCollection.filterByOvernightFromPreviousRange(firstDay)
    const fromPreviousDuration = overnightFromPrevious.durationOvernightFromPreviousWeek()

    return {
      shifts: [...withinRange.shifts, ...overnightToNext.shifts, ...overnightFromPrevious.shifts],
      withinRangeDuration,
      toNextDuration,
      fromPreviousDuration,
    }
  }

  shiftsPerRange(firstDay: moment.Moment, lastDay: moment.Moment): Shift[] {
    return this.getShiftsAndDurationsForRange(firstDay, lastDay).shifts
  }

  hoursPerRangeAsMilliseconds(
    firstDay: moment.Moment,
    lastDay: moment.Moment,
    plannedBreaks: PlannedBreaks
  ): number {
    const { withinRangeDuration, toNextDuration, fromPreviousDuration, shifts } =
      this.getShiftsAndDurationsForRange(firstDay, lastDay)

    // TODO: Use plannedBreaksCollection once 'dev_shift_management_graphql_migration' is done
    const shiftIds = shifts.map((s) => s.id)
    const selectedPlannedBreaks = this.plannedBreaksCollection.plannedBreaks.length
      ? this.plannedBreaksCollection.filterByShifts(shiftIds)
      : plannedBreaks.filterByShifts(shiftIds)

    const plannedBreaksMinutes = selectedPlannedBreaks.reduce((acc, plannedBreak) => {
      const { duration, breakConfigurationPaid } = plannedBreak
      if (!breakConfigurationPaid && duration) return acc + duration
      return acc
    }, 0)

    const plannedBreaksDuration = moment.duration(plannedBreaksMinutes, 'minutes')

    const totalDuration = withinRangeDuration
      .add(toNextDuration)
      .add(fromPreviousDuration)
      .subtract(plannedBreaksDuration)

    return totalDuration.asMilliseconds()
  }

  addShifts(shifts: Shift[]): ShiftsRow {
    const groupedShifts = groupBy(shifts, (shift) => shift.employeeId)

    for (const key in groupedShifts) {
      const index = this.employees.findIndex((emp) => emp.id === +key)
      if (index !== -1) {
        this.employees[index]?.addShifts(groupedShifts[key] || [])
      }
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  resetShifts(shifts: Shift[]): ShiftsRow {
    const groupedShifts = groupBy(shifts, (shift) => shift.employeeId)

    for (const key in groupedShifts) {
      const index = this.employees.findIndex((emp) => emp.id === +key)
      if (index !== -1) {
        this.employees[index]?.resetShifts(groupedShifts[key] || [])
      }
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  addRestDays(restDays: RestDay[]): ShiftsRow {
    const groupedRestDays = groupBy(restDays, (restDay) => restDay.employeeId)

    for (const key in groupedRestDays) {
      const index = this.employees.findIndex((emp) => emp.id === +key)
      if (index !== -1) {
        this.employees[index]!.addRestDays(groupedRestDays[key] || [])
      }
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  destroyRestDay(restDay: RestDay): ShiftsRow {
    const index = findIndex(this.employees, (emp) => emp.id === restDay.employeeId)
    if (index !== -1) {
      this.employees[index]!.destroyRestDay(restDay.id)
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  destroyRestDays(restDays: RestDay[]): ShiftsRow {
    const groupedRestDays = groupBy(restDays, (restDay: RestDay) => restDay.employeeId)

    for (const key in groupedRestDays) {
      const index = this.employees.findIndex((emp) => emp.id === +key)
      if (index !== -1) {
        const restDays = groupedRestDays[key] || []
        this.employees[index]!.destroyRestDays(restDays.map((restDay) => restDay.id))
      }
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  destroyShift(shift: Shift): ShiftsRow {
    const index = this.employees.findIndex((emp) => emp.id === shift.employeeId)
    if (index !== -1) {
      this.employees[index]!.destroyShift(shift.id)
    }

    return new ShiftsRow({ ...this, ...this.employees })
  }

  destroyShifts(shifts: Shift[]): ShiftsRow {
    const groupedShifts = groupBy(shifts, (shift) => shift.employeeId)

    this.employees.forEach((employee: EmployeeWithShifts) => {
      const shifts = groupedShifts[employee.id] || []
      employee.destroyShifts(shifts.map((s) => s.id))
    })

    return new ShiftsRow({ ...this, ...this.employees })
  }

  hasDrafts() {
    const drafts = this.shifts.filterByDraft()

    return !!drafts.length
  }

  get canManage(): boolean {
    return !!this.permissions.create
  }

  get canCreateLeaves(): boolean {
    return !!this.permissions.createLeave
  }

  get hasNoneShiftManagementPlanning(): boolean {
    return this.planningVersions.filterByPlanningTool(PLANNING_TOOL.shiftManagement).length === 0
  }

  isDisabled(view: View, date: moment.Moment): boolean {
    // In work areas, for the moment cells are never disabled, only empty
    if (view.isByAreas) return false

    const employee = this.employees.find((emp) => emp.id === this.id)
    const employeeModel = employee?.employeeModel
    const planningVersion = employee?.planningVersions.planningVersionWithinDate(date)

    if (!employee || !employeeModel) return true

    return (
      !planningVersion?.isShiftManagementTool ||
      (employeeModel.isTerminated && date > employeeModel.terminationDate!)
    )
  }

  isRowArchivedWorkArea(view: View): boolean {
    if (view.isByEmployees) return false

    const workArea = workAreas.find({ id: this.id })

    if (!workArea) return false

    return workArea.isArchived
  }

  updateShiftsColor(defaultShiftId: number, color: Colors): ShiftsRow {
    const employees = this.employees.map((employee) => {
      const shiftsToColorIds = employee.shifts.filterByDefaultShift(defaultShiftId).map((s) => s.id)
      return employee.updateShiftsColor(shiftsToColorIds, color)
    })

    return new ShiftsRow({ ...this, employees })
  }

  lastNightShift(date: moment.Moment): Shift | null {
    if (!this.visibleShifts.length) return null

    return this.visibleShifts.reduce((lastNightShift: Shift | null, shift) => {
      if (
        shift.isBeginNightShift(date) &&
        (!lastNightShift || shift.currentEndDate.isAfter(lastNightShift.currentEndDate))
      ) {
        lastNightShift = shift
      }

      return lastNightShift
    }, null)
  }

  hoursInTheNextPeriod(date: moment.Moment): number {
    return Number(this.lastNightShift(date)?.currentEndDate.format('HH')) || 0
  }

  firstShift(date: moment.Moment): Shift | null {
    if (!this.visibleShifts.length) return null

    return this.visibleShifts.reduce((firstShift: Shift | null, shift) => {
      if (
        !shift.isEndNightShift(date) &&
        (!firstShift || shift.currentStartDate.isBefore(firstShift.currentStartDate))
      ) {
        firstShift = shift
      }

      return firstShift
    }, null)
  }

  startingHour(date: moment.Moment): number | null {
    const value = Number(this.firstShift(date)?.currentStartDate.format('HH'))
    return isNaN(value) ? null : value
  }
}

export default ShiftsRow
